<script setup lang="ts">
const { t } = useI18n()
const route = useRoute()
const { toast } = useToast()
const { guestChoice: backInStockGuestChoice } = useBackInStock()

// `seo: false` disables the i18n module's auto-injected canonical / hreflang /
// og:locale:alternate. Those are emitted per-page by `useSeo()` and restricted
// to the 7 exalted-only locales (via `appLocales`) rather than every locale
// registered in the shared i18n layer (~25). Keeping the call only for
// `htmlAttrs.lang/dir` which the rest of the head config still depends on.
// @nuxtjs/i18n v10 renamed these options; the v8 names this call used to pass
// (`addSeoAttributes: false`, …) were ignored, `seo` defaulted to true, and every
// page shipped a second, bare-language hreflang set (`hreflang="de"`) built by
// swapping the locale prefix — wrong on any page whose slug is translated.
const i18nHead = useLocaleHead({
  dir: true,
  lang: true,
  seo: false,
})

// Last-resort description for the handful of routes that never call
// `useSeo().setSeoMeta` with one of their own (account/auth/search/wishlist,
// the seller pages). Every indexable page is expected to override it — when
// two pages share this string in a SERP, that page is missing its description,
// not being served a good default.
const DEFAULT_DESCRIPTION = 'EXALTED - Do The Things You Can\'t Do.'

useHead({
  htmlAttrs: {
    lang: i18nHead.value?.htmlAttrs?.lang ?? 'en',
    dir: i18nHead.value?.htmlAttrs?.dir ?? 'ltr',
    class: 'scroll-smooth xs:text-[16px] text-[14px]',
  },
  bodyAttrs: {
    class: 'font-gilam antialiased tracking-[0.0125em]',
  },
  // `layouts.title` is `{0} | EXALTED` — a positional placeholder, so a route's
  // own title is passed as a list arg. With no route title we fall back to the
  // brand title (`pages.title.index`), which is already fully branded, so it's
  // emitted verbatim rather than re-wrapped (which would repeat "EXALTED").
  title: computed(() => {
    const routeTitle = route.meta?.title as string | undefined
    return routeTitle ? t('layouts.title', [t(routeTitle)]) : t('pages.title.index')
  }),
  meta: [
    ...(i18nHead.value.meta || []),
    ...(useRuntimeConfig().public.facebookDomainVerification ? [{ name: 'facebook-domain-verification', content: useRuntimeConfig().public.facebookDomainVerification }] : []),
    ...(useRuntimeConfig().public.googleSiteVerification ? [{ name: 'google-site-verification', content: useRuntimeConfig().public.googleSiteVerification }] : []),
    { name: 'viewport', content: 'width=device-width,initial-scale=1,viewport-fit=cover' },
    { name: 'charset', content: 'UTF-8' },
    { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
    { property: 'og:image', content: '/image.png' },
    // The og:/twitter: descriptions mirror `name="description"` so a page that
    // falls back to the default still shares with *some* description instead of
    // an image and a bare title. `setSeoMeta` overrides all three together, so a
    // page that sets its own never mixes its description with this one.
    // (A second `{ name: 'description', content: 'description' }` entry used to
    // sit above these and shipped the literal word as a meta description.)
    { name: 'description', content: DEFAULT_DESCRIPTION },
    { property: 'og:description', content: DEFAULT_DESCRIPTION },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: '/image.png' },
    { name: 'twitter:description', content: DEFAULT_DESCRIPTION },
  ],
  link: [
    ...(i18nHead.value.link || []),
    // Body text renders in Gilam 400 (`font-gilam` on <body>), so the regular
    // weight is on the critical path. Preloaded from here rather than via
    // `fonts.families[].preload`, which nuxt-vitalizer strips out of the build
    // manifest. `crossorigin` is mandatory on font preloads — without it the
    // preloaded response is not reused by the CSS-driven fetch.
    { rel: 'preload', as: 'font', type: 'font/woff2', href: '/fonts/gilam-regular.woff2', crossorigin: 'anonymous', fetchpriority: 'high' },
    { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico', sizes: 'any' },
    { rel: 'icon', type: 'image/png', href: '/icon.png', sizes: 'any' },
    { rel: 'icon', type: 'image/svg+xml', href: '/icon.svg', sizes: 'any' },
    { rel: 'apple-touch-icon', type: 'image/png', href: '/apple-touch-icon.png', sizes: 'any' },
  ],
  script: [
    // Client-Side Adapter for the Queue-Fair virtual waiting room (
    // https://queue-fair.com/developers). Loads on every page — it's a no-op
    // on pages that don't match a configured Queue — so it's always in place
    // for whichever Activation Rules are live in the Queue-Fair Portal.
    // Gated behind the `queueFairEnabled` runtime flag
    // (NUXT_PUBLIC_QUEUE_FAIR_ENABLED, string 'true'/'false') so the queue can
    // be switched on/off per environment without a code change.
    // Compared via String(): Nitro's runtime env override passes the value
    // through destr(), so the "true" we set in the k8s configmap arrives here
    // as the boolean true, never the string. A bare === 'true' therefore never
    // matched and the adapter stayed off in every environment. Truthiness is
    // not an option either: the build-time default is the string 'false'.
    ...(String(useRuntimeConfig().public.queueFairEnabled) === 'true'
      ? [{ 'key': 'queue-fair-adapter', 'tagPriority': 'critical', 'data-queue-fair-client': 'exalted', 'src': 'https://files.queue-fair.net/queue-fair-adapter.js' }]
      : []),
  ],
})

/**
 * Whether the gift-with-order card is worth mounting. The card plus its three
 * pickers (`GiftOptionCard`, `GiftColourPicker`, `GiftSizePicker`) and
 * `useGiftSelection` were ~17.3 KB raw (~4.5 KB brotli) of the entry chunk on
 * every route, although they only matter to a cart that is in a gift promotion.
 *
 * It flips on the first sign of a gift on the order, not on the first open:
 * nuxt-vitalizer strips prefetch/modulepreload, so a lazy chunk is fetched cold
 * at the moment it is mounted, and mounting it while the cart is still being
 * filled means the card is already there when a gift unlocks. Sticky: nothing
 * sets it back, so reka's closing animation and everything the card holds
 * survive a close, and the chunk is fetched at most once.
 *
 * Client-only: the card renders inside `ClientOnly` and never on the server,
 * and these are the composables `ScratchTrigger` instantiates there anyway.
 *
 * The cost of being lazy: if that chunk cannot be fetched at all (a deploy
 * replaced it mid-session, a dropped connection), there is no card, and a
 * required pending gift keeps "Pay now" disabled until the shopper reloads the page —
 * the browser caches a failed dynamic import, so nothing in the running app
 * can re-fetch it. `AsyncGiftUnlockDialog` below records that failure;
 * `recoverMissingGiftCard` in this block decides what it is worth doing about
 * it, and for whom.
 */
const giftDialogWanted = ref(false)

/**
 * The card's chunk could not be fetched (`AsyncGiftUnlockDialog`).
 *
 * Sticky for the rest of the page life, and it has to be: Vue memoises the
 * load in `pendingRequest` and only `retry()` clears it, which this never
 * calls — so the wrapper's promise stays unsettled, the card can never mount
 * here, and `onError` never runs a second time.
 */
const giftChunkFailed = ref(false)

/** Survives the reload it guards — that is the whole point of it. */
const GIFT_CHUNK_RELOAD_KEY = 'gift-unlock-chunk-reloaded'

/**
 * Spends this tab's single reload, and says whether it was there to spend.
 *
 * `false` on the second failure, and `false` whenever sessionStorage cannot be
 * read or written (Safari private mode, blocked site data): with no mark that
 * outlives the reload, the fresh page would reload again on the same failure,
 * and again. `reloadNuxtApp`'s own guard is not enough on its own — it is a
 * 10s TTL per path, and an import on a dead connection can take longer than
 * that to reject.
 */
function claimGiftChunkReload(): boolean {
  try {
    if (window.sessionStorage.getItem(GIFT_CHUNK_RELOAD_KEY) === '1')
      return false
    window.sessionStorage.setItem(GIFT_CHUNK_RELOAD_KEY, '1')
    return true
  }
  catch {
    return false
  }
}

if (import.meta.client) {
  const { isOpen, close } = useGiftUnlockDialog()
  const { entries, pendingGifts } = useGiftProgress()
  const drawers = useDrawers()
  watchEffect(() => {
    // The flag is read first, so once it has flipped this effect re-runs on the
    // flag alone and stops tracking the order.
    if (giftDialogWanted.value)
      return
    if (isOpen.value || pendingGifts.value.length > 0 || entries.value.length > 0)
      giftDialogWanted.value = true
  })

  // "Drawer closed -> close the card" is the card's own rule
  // (GiftUnlockDialog.vue), and the card only exists once its chunk has landed.
  // In that window the drawer can still be dismissed — scrim, X, and Escape,
  // which with no card in the DOM to claim it belongs to the drawer — and
  // nothing would close the dialog: the arriving card would then draw itself as
  // a page-wide modal over the page the shopper went back to. Recorded with the
  // card's own condition (`inDrawer`: the drawer open AND its panel in the DOM
  // when the dialog opened — checkout renders no drawer), so once the card is
  // mounted the two rules agree and closing twice is one close.
  let openedInDrawer = false
  watch(isOpen, (open) => {
    if (open)
      openedInDrawer = drawers.value.cart.open && Boolean(document.getElementById('cart-drawer-panel'))
  })
  watch(() => drawers.value.cart.open, (open) => {
    if (!open && openedInDrawer && isOpen.value)
      close()
  })

  /**
   * What a chunk that never arrived is worth doing about — nothing at all for
   * most of the shoppers it happens to.
   *
   * Never a reload on checkout: it would throw away a part-typed address form
   * (checkout writes the address to the order on submit, not per field) and
   * leave the shopper the same blocked "Pay now". The copy then asks them to
   * refresh by hand, which costs them that same form — accepted, because it is
   * the only way back to a card they cannot check out without, and asking lets
   * them finish the form first.
   *
   * Everywhere else the reload is taken without asking: it is the only thing
   * that can bring the card back, and it costs a refetch plus every `useState`
   * in the app — the cart drawer closes and the scroll position goes, which is
   * still cheaper than a gift the shopper cannot claim. `persistState` would
   * not save those: Nuxt reads `nuxt:reload:state` back only under
   * `experimental.restoreState`, which is off (its default). `force` because
   * `claimGiftChunkReload` is the authority on "once" and Nuxt's own 10s/path
   * window could otherwise swallow the one reload this tab gets; no path is
   * passed, so the URL reloads with its query and hash intact.
   */
  function recoverMissingGiftCard(): void {
    if (!giftChunkFailed.value)
      return
    if (!route.path.includes('/checkout') && claimGiftChunkReload()) {
      reloadNuxtApp({ force: true })
      return
    }
    toast({ variant: 'destructive', title: t('gift.modal.load_failed') })
    // The card cannot mount in this page life, so an open dialog is a lie —
    // and closing it re-arms the `isOpen` watcher below, so the next press of
    // the pending-gift row says the same thing again instead of nothing.
    if (isOpen.value)
      close()
  }

  // Acted on when the missing card actually costs the shopper something, not
  // when it fails to load: `giftDialogWanted` flips for any cart in a gift
  // promotion, a "spend 500 more" bar with nothing to claim included, and
  // reloading the page under that shopper — or showing them a red error — is
  // worse than the card they never asked for. The two triggers are the one
  // checkout gates on (a pending unit still to choose, pages/checkout/index.vue)
  // and an explicit ask for the card, which is also the only one that repeats.
  //
  // Both have to be watched rather than handled in `onError`: the chunk is
  // fetched at the first sign of a gift, usually long before one is pending,
  // and `onError` cannot run again (see `giftChunkFailed`). Until it does run
  // once, each of these is a boolean read on a change the app already makes.
  watch([giftChunkFailed, () => pendingGifts.value.some(giftRequiresSelection)], ([, blocked]) => {
    if (blocked)
      recoverMissingGiftCard()
  })
  watch(isOpen, (open) => {
    if (open)
      recoverMissingGiftCard()
  })
}

/**
 * The card, and what happens when its chunk cannot be fetched.
 *
 * Vue hands `onError` the loader's rejection and nothing else, which is why
 * this is here rather than a `<NuxtErrorBoundary>` around the Lazy component:
 * a boundary catches the load failure too (the async wrapper reports it as
 * ASYNC_COMPONENT_LOADER through the same `errorCaptured` chain as any other
 * error), but it would see everything the card throws once it renders as well,
 * and a reload fixes a missing chunk, not a bug in the card. `<Lazy…>` has no
 * handler to hook — Nuxt compiles the prefix to `defineAsyncComponent(loader)`
 * with a bare loader — so taking the wrapper over is the whole difference.
 *
 * It only records the failure. Which shopper it is worth acting on, and how,
 * is `recoverMissingGiftCard` above: this runs the moment the chunk is
 * fetched — the first sign of a gift on the order — which is usually long
 * before anyone is blocked by the card not being there.
 *
 * Neither `retry()` nor `fail()` is called. The browser remembers a failed
 * dynamic import, so `retry()` re-runs the same `import()` and rejects out of
 * the module map without a new request. `fail()` buys nothing over leaving the
 * promise unsettled — both render nothing — and only adds an unhandled-error
 * log: it re-throws into `handleError`, and by the time this card can mount
 * (inside `ClientOnly`, so from `onMounted` onwards) Nuxt has already dropped
 * its own `errorHandler`, which it clears on `app:suspense:resolve`. That
 * handler is the one that sets `payload.error`, so the full-page error screen
 * is a hazard only for an async component that can settle before hydration
 * finishes — this one cannot.
 */
const AsyncGiftUnlockDialog = defineAsyncComponent({
  loader: () => import('~/components/gift/GiftUnlockDialog.vue'),
  onError: (error) => {
    console.error('Gift dialog chunk failed to load', error)
    giftChunkFailed.value = true
  },
})
</script>

<template>
  <RdxConfigProvider>
    <NuxtLoadingIndicator color="black" />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    <!-- Here rather than in a layout: checkout renders without one and still
         has to open the gift dialog (its pending-gift gate). Lazy and mounted
         only once `giftDialogWanted` says the cart is in a gift promotion (see
         above), so the card's JS leaves the every-route entry chunk; once
         mounted it stays mounted. Our own async wrapper rather than
         `<LazyGiftUnlockDialog>`, so a chunk that never arrives lands
         somewhere (see `AsyncGiftUnlockDialog`). -->
    <ClientOnly>
      <LazyBackInStockDialog v-if="backInStockGuestChoice" />
      <RewardsHost />
      <InfluencerShareButton />
      <AsyncGiftUnlockDialog v-if="giftDialogWanted" />
      <!-- Scratch to win: the always-mounted trigger (timer, exit intent, the
           strip's opener) that lazy-loads the overlay. Here rather than in a layout
           so outfit pages (layout: false) and the preview page get it too; checkout,
           auth and preview are refused by path in useScratchTrigger. -->
      <ScratchTrigger />
    </ClientOnly>

    <UiToaster
      :label="t('aria.notification.label')"
      :viewport-label="(hotkey: string) => t('aria.notifications.label', { hotkey })"
      :close-label="t('general.close')"
    />
  </RdxConfigProvider>
</template>
